import { heliosApi } from '.';
import { TAG_TYPES } from 'types/services';
import type { GetDatasourcesReq, GetDatasourcesRes, GetStatusesReq, GetStatusesRes } from 'types';
import type { heliosDataSourcesDataScheme } from 'utils/table/admin/helios/dataSources';
import type { GetTimeZonesRes } from 'types/datasources';

export const dataSourcesService = heliosApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getStatuses: builder.query<GetStatusesRes, GetStatusesReq>({
      query: (params) => ({
        url: 'DatasourceStates',
        params,
      }),
    }),
    getDatasources: builder.query<GetDatasourcesRes, GetDatasourcesReq<typeof heliosDataSourcesDataScheme>>({
      query: (data) => ({
        url: 'Datasources',
        params: data,
      }),
      providesTags: [TAG_TYPES.DATASOURCES],
    }),
    createDatasource: builder.mutation<
      GetDatasourcesRes['items'][number],
      {
        name: string;
        wsUrlBasic: string;
        wsUrlDomain: string;
        wsUrlGoogle: string;
        wsUrlMicrosoft: string;
        timeZoneId: string;
        wsProfile: string;
        basicLoginPurpose: string;
        domainLoginPurpose: string;
        googleLoginPurpose: string;
        microsoftLoginPurpose: string;
        maxConcurrentConnections: number;
        useUserDefaultAccount: boolean;
        runFunctionUnderOriginalUser: boolean;
      }
    >({
      query: (data) => ({
        url: 'Datasources',
        method: 'POST',
        body: data,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: [TAG_TYPES.DATASOURCES],
    }),
    getDatasource: builder.query<
      {
        id: string;
        name: string;
        number: number;
        wsUrlBasic: string;
        wsUrlDomain: string;
        wsUrlGoogle: string;
        wsUrlMicrosoft: string;
        wsProfile: string;
        timeZoneId: string;
        basicLoginPurpose: string;
        domainLoginPurpose: string;
        googleLoginPurpose: string;
        microsoftLoginPurpose: string;
        maxConcurrentConnections: number;
        useUserDefaultAccount: boolean;
        runFunctionUnderOriginalUser: boolean;
      },
      {
        datasourceId: string;
      }
    >({
      query: ({ datasourceId }) => ({
        url: `Datasources/${datasourceId}`,
      }),
      providesTags: [TAG_TYPES.DATASOURCES],
    }),
    getTimezones: builder.query<GetTimeZonesRes, void>({
      query: () => ({
        url: 'DatasourceTimeZones',
      }),
    }),
    updateDatasource: builder.mutation<
      GetDatasourcesRes['items'][number],
      {
        id: string;
        name: string;
        wsUrlBasic: string;
        wsUrlDomain: string;
        wsUrlGoogle: string;
        timeZoneId: string;
        wsProfile: string;
        basicLoginPurpose: string;
        domainLoginPurpose: string;
        googleLoginPurpose: string;
        maxConcurrentConnections: number;
        useUserDefaultAccount: boolean;
        runFunctionUnderOriginalUser: boolean;
      }
    >({
      query: (data) => ({
        url: 'Datasources',
        method: 'PUT',
        body: data,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      invalidatesTags: [TAG_TYPES.DATASOURCES],
    }),
    deleteDatasource: builder.mutation<void, { ids: string[] }>({
      query: (data) => ({
        url: `Datasources`,
        method: 'DELETE',
        params: data,
      }),
      invalidatesTags: [TAG_TYPES.DATASOURCES],
    }),
  }),
});
export const {
  useGetDatasourcesQuery,
  useGetStatusesQuery,
  useLazyGetDatasourcesQuery,
  useLazyGetStatusesQuery,
  useGetTimezonesQuery,
  useLazyGetTimezonesQuery,
  useCreateDatasourceMutation,
  useGetDatasourceQuery,
  useLazyGetDatasourceQuery,
  useUpdateDatasourceMutation,
  useDeleteDatasourceMutation,
} = dataSourcesService;
